import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import img from '../images/Signup/verify.png';

function Verify() {
    const [code, setCode] = useState(new Array(6).fill(''));
    const [countdown, setCountdown] = useState(60);
    const location = useLocation();
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const { sessionId, inputType, destination } = location.state || {};
    const [verificationData, setVerificationData] = useState(null);

    useEffect(() => {
        // Get stored data from localStorage
        const storedData = localStorage.getItem('verificationData');
        if (storedData) {
            setVerificationData(JSON.parse(storedData));
        }

        if (!sessionId) {
            navigate('/login', { replace: true });
            return;
        }

        window.scrollTo(0, 0);

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    localStorage.removeItem('verificationData');
                    navigate('/signup');
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [sessionId, navigate]);

    const handleChange = (element, index) => {
        const value = element.value.replace(/\D/, ''); // Only allow numeric input
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);

        // Move to next input
        if (value && element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !e.target.value && e.target.previousSibling) {
            e.target.previousSibling.focus();
        }
    };

    const handleSubmit = async () => {
        const otp = code.join('');
        
        try {
            if (!sessionId) {
                alert('Session expired. Please restart registration.');
                navigate('/signup');
                return;
            }

            const response = await fetch(`${apiBaseUrl}/user/Verify/BLACKBATON_2526`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    sessionId: sessionId, 
                    otp: otp 
                })
            });

            const data = await response.json();

            if (data.success) {
                alert('Registration Successful!');
                
                // Clear localStorage
                localStorage.removeItem('verificationData');
                
                // Clear the input fields
                setCode(new Array(6).fill(''));
                
                // Navigate to login page
                navigate('/login');
            } else {
                alert(data.error || 'Invalid OTP. Please check the code and try again.');
            }
        } catch (err) {
            console.error('Error verifying OTP:', err);
            alert('OTP verification failed');
        }
    };

    const resendOTP = async () => {
        try {
            if (!sessionId) {
                alert('Session expired. Please restart registration.');
                navigate('/signup');
                return;
            }

            const response = await fetch(`${apiBaseUrl}/user/ResendOTP/BLACKBATON_2526`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sessionId: sessionId
                })
            });

            const data = await response.json();
            
            if (data.success) {
                alert('OTP resent successfully!');
                
                // Update OTP in localStorage
                if (verificationData) {
                    const updatedData = { ...verificationData, otp: data.otp };
                    localStorage.setItem('verificationData', JSON.stringify(updatedData));
                    setVerificationData(updatedData);
                }
                
                setCountdown(60); // Reset countdown
            } else {
                alert(data.error || 'Failed to resend OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error resending OTP:', error);
            alert('Error resending OTP');
        }
    };

    const cancelRegistration = () => {
        localStorage.removeItem('verificationData');
        navigate('/signup');
    };

    return (
        <div className="min-h-screen flex flex-row">
            {/* Left Section */}
            <div className="w-1/2 lg:flex hidden justify-center items-center">
                <div className='h-[680px] w-[80%]'>
                    <img src={img} alt="img" className='w-full h-full' />
                </div>
            </div>

            {/* Right Section */}
            <div className="lg:w-1/2 w-full flex justify-center items-center flex-col lg:gap-8 gap-5">
                <div className='flex flex-col gap-4 lg:px-0 px-3'>
                    <span className='lg:text-5xl text-3xl font-[500] font-notoSerif'>Verify with OTP</span>
                    <span className='lg:text-base text-sm font-[400] font-montserrat text-[#3D3D3D]'>
                        To ensure your security, please enter the One-Time Password (OTP) <br /> 
                        sent to your {inputType === 'email' ? 'email' : 'mobile number'}
                        {destination && ` (${destination})`}
                    </span>
                </div>

                <div className='flex flex-col gap-6 lg:px-0 px-3'>
                    <div className='flex flex-col gap-4 justify-center items-center'>
                        <div className='flex flex-row lg:gap-5 gap-1'>
                            {code.map((digit, index) => (
                                <input
                                    key={index}
                                    type='text'
                                    maxLength='1'
                                    value={digit}
                                    onChange={(e) => handleChange(e.target, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    className='w-12 h-11 border-2 border-[#D7D7D7] rounded-[5px] text-center text-xl'
                                />
                            ))}
                        </div>
                    </div>

                    <div className='flex flex-col gap-2'>
                        {/* Submit Button */}
                        <button
                            className='lg:w-[430px] w-[350px] h-[48px] rounded-[12px] bg-black flex justify-center items-center font-[600] font-montserrat text-base text-white'
                            onClick={handleSubmit}
                        >
                            Verify & Complete Registration
                        </button>

                        {/* Cancel Button */}
                        <button
                            className='lg:w-[430px] w-[350px] h-[48px] rounded-[12px] border-2 border-[#EDEDED] flex flex-row gap-2 justify-center items-center cursor-pointer'
                            onClick={cancelRegistration}
                        >
                            Cancel Registration
                        </button>
                        
                        {/* Countdown Timer */}
                        <span className='text-base font-[400] font-display text-[#8B8B8B] text-center'>
                            {`Time remaining: ${countdown} sec`}
                        </span>

                        {/* Resend OTP Link */}
                        <span className='text-sm font-[400] font-montserrat text-[#49475A] text-center'>
                            Having difficulties with OTP? &nbsp;
                            <span className='font-[800] cursor-pointer' onClick={resendOTP}>
                                Resend OTP
                            </span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Verify;